import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.9);
  const rewardSent = useRef(false);
  const listenAccum = useRef(0);

  const playSong = useCallback((song, list = null) => {
    const q = list || [song];
    const idx = q.findIndex((s) => s.id === song.id);
    setQueue(q);
    setIndex(idx < 0 ? 0 : idx);
    setCurrent(song);
    rewardSent.current = false;
    listenAccum.current = 0;
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    a.src = current.stream_url || `${api.defaults.baseURL}/stream/${current.id}`;
    a.load();
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [current]);

  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; }, [volume]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a || !current) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  const next = useCallback(() => {
    if (index < queue.length - 1) { const n = index + 1; setIndex(n); setCurrent(queue[n]); rewardSent.current = false; listenAccum.current = 0; }
  }, [index, queue]);

  const prev = useCallback(() => {
    if (index > 0) { const n = index - 1; setIndex(n); setCurrent(queue[n]); rewardSent.current = false; listenAccum.current = 0; }
  }, [index]);

  const seek = (t) => { if (audioRef.current) audioRef.current.currentTime = t; };

  const onTimeUpdate = (e) => {
    const a = e.target;
    setProgress(a.currentTime);
    listenAccum.current = a.currentTime;
    // Register a qualifying listen once after 20s of playback
    if (!rewardSent.current && a.currentTime >= 20 && current) {
      rewardSent.current = true;
      api.post(`/songs/${current.id}/play`, { seconds_listened: Math.floor(a.currentTime) })
        .then(({ data }) => {
          if (data.reward > 0 && window.__jxOnReward) window.__jxOnReward(data.reward);
        }).catch(() => {});
    }
  };

  return (
    <PlayerContext.Provider value={{ current, playing, progress, duration, volume, setVolume, queue, index, playSong, toggle, next, prev, seek, audioRef }}>
      {children}
      <audio
        ref={audioRef}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
        onEnded={next}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        preload="metadata"
      />
    </PlayerContext.Provider>
  );
}
